import React, { useEffect, useMemo, useState } from "react";
import { formatISTDateTime, getISTDateInputValue } from "../utils/dateTime";

const BASE_URL = "/v1";

function GstItemsModal({ show, onClose }) {
  const today = useMemo(() => getISTDateInputValue(), []);
  const sevenDaysAgo = useMemo(() => {
    const [year, month, day] = today.split("-").map(Number);
    return new Date(Date.UTC(year, month - 1, day - 6)).toISOString().slice(0, 10);
  }, [today]);

  const [fromDate, setFromDate] = useState(sevenDaysAgo);
  const [toDate, setToDate] = useState(today);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("");

  const normalizeRows = (data) => {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.items)) return data.items;
    if (Array.isArray(data?.content)) return data.content;
    if (Array.isArray(data?.data)) return data.data;
    if (Array.isArray(data?.rows)) return data.rows;
    return [];
  };

  const loadItems = async (customFrom = fromDate, customTo = toDate) => {
    if (!customFrom || !customTo) return;

    try {
      setLoading(true);
      setMessage("");
      setMessageType("");

      const token = localStorage.getItem("token");
      const res = await fetch(
        `${BASE_URL}/bills/items?from=${encodeURIComponent(customFrom)}&to=${encodeURIComponent(customTo)}`,
        {
          headers: {
            Authorization: token ? `Bearer ${token}` : ""
          }
        }
      );

      if (!res.ok) {
        throw new Error("Failed to load GST items");
      }

      const data = await res.json();
      setItems(normalizeRows(data));
    } catch (err) {
      console.error(err);
      setItems([]);
      setMessage("Failed to load GST items");
      setMessageType("danger");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!show) return;
    setFromDate(sevenDaysAgo);
    setToDate(today);
    loadItems(sevenDaysAgo, today);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  if (!show) return null;

  const rows = items;

  const totals = rows.reduce(
    (acc, item) => {
      acc.mrp += Number(item?.mrp ?? item?.price ?? 0) || 0;
      acc.quantity += Number(item?.quantity ?? 0) || 0;
      acc.discountAmount += Number(item?.discountAmount ?? item?.itemDiscountAmount ?? 0) || 0;
      acc.gstAmount += Number(item?.gstAmount ?? 0) || 0;
      acc.finalValue += Number(item?.finalValue ?? item?.total ?? 0) || 0;
      return acc;
    },
    {
      mrp: 0,
      quantity: 0,
      discountAmount: 0,
      gstAmount: 0,
      finalValue: 0
    }
  );

  return (
    <>
      <div className="modal-backdrop fade show"></div>

      <div className="modal show d-block" tabIndex="-1">
        <div className="modal-dialog modal-xl">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">GST Report</h5>
              <button className="btn-close" onClick={onClose}></button>
            </div>

            <div className="modal-body">
              {loading && (
                <div className="modal-loading-overlay">
                  <div className="text-center">
                    <div className="spinner-border" role="status"></div>
                    <p className="mt-2 mb-0">Loading GST items...</p>
                  </div>
                </div>
              )}

              {message && (
                <div className={`alert alert-${messageType || "info"} py-2`}>
                  {message}
                </div>
              )}

              <div className="row g-2 align-items-end mb-3">
                <div className="col-md-4">
                  <label className="form-label mb-1">From Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                  />
                </div>

                <div className="col-md-4">
                  <label className="form-label mb-1">To Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                  />
                </div>

                <div className="col-md-4 d-flex gap-2">
                  <button
                    type="button"
                    className="btn btn-primary flex-grow-1"
                    onClick={() => loadItems(fromDate, toDate)}
                    disabled={loading}
                  >
                    {loading ? "Loading..." : "Refresh"}
                  </button>

                  <button
                    type="button"
                    className="btn btn-secondary flex-grow-1"
                    onClick={() => {
                      setFromDate(sevenDaysAgo);
                      setToDate(today);
                      loadItems(sevenDaysAgo, today);
                    }}
                    disabled={loading}
                  >
                    Reset
                  </button>
                </div>
              </div>

              <div
                className="border rounded"
                style={{ maxHeight: "58vh", overflowY: "auto" }}
              >
                <table className="table table-sm mb-0">
                  <thead className="table-light sticky-top" style={{ zIndex: 1 }}>
                    <tr>
                      <th>Item</th>
                      <th>Receipt</th>
                      <th>Date</th>
                      <th>Qty</th>
                      <th>MRP</th>
                      <th>Disc Amt</th>
                      <th>Disc %</th>
                      <th>GST %</th>
                      <th>GST Amt</th>
                      <th>Final Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.length > 0 ? (
                      rows.map((item, index) => {
                        const itemName =
                          item?.itemName ||
                          item?.productName ||
                          item?.name ||
                          item?.product ||
                          `Item ${index + 1}`;
                        const receiptNo = item?.receiptNo || item?.billNo || item?.billReceiptNo || "-";
                        const dateValue = item?.createdAt || item?.billDate || item?.date;
                        const formattedDate = dateValue ? formatISTDateTime(dateValue) : "-";
                        const quantity = Number(item?.quantity ?? 0) || 0;
                        const mrp = Number(item?.mrp ?? item?.price ?? 0) || 0;
                        const discountAmount = Number(item?.discountAmount ?? item?.itemDiscountAmount ?? 0) || 0;
                        const discountPercent = Number(item?.discountPercent ?? item?.itemDiscount ?? 0) || 0;
                        const gstRate = Number(item?.gstRate ?? 0) || 0;
                        const gstAmount = Number(item?.gstAmount ?? 0) || 0;
                        const finalValue = Number(item?.finalValue ?? item?.total ?? 0) || 0;

                        return (
                          <tr key={`${receiptNo}-${index}`}>
                            <td style={{ minWidth: "180px" }}>{itemName}</td>
                            <td>{receiptNo}</td>
                            <td>{formattedDate}</td>
                            <td>{quantity}</td>
                            <td>₹{mrp.toFixed(2)}</td>
                            <td>₹{discountAmount.toFixed(2)}</td>
                            <td>{discountPercent}%</td>
                            <td>{gstRate}%</td>
                            <td>₹{gstAmount.toFixed(2)}</td>
                            <td>₹{finalValue.toFixed(2)}</td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="12" className="text-center py-4">
                          No GST items found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="mt-3 border rounded p-3 bg-light">
                <div className="row g-2">
                  <div className="col-md-4">
                    <strong>Total MRP:</strong> ₹{totals.mrp.toFixed(2)}
                  </div>
                  <div className="col-md-4">
                    <strong>Total Quantity:</strong> {totals.quantity.toFixed(0)}
                  </div>
                  <div className="col-md-4">
                    <strong>Total GST:</strong> ₹{totals.gstAmount.toFixed(2)}
                  </div>
                  <div className="col-md-4">
                    <strong>Total Discount:</strong> ₹{totals.discountAmount.toFixed(2)}
                  </div>
                  <div className="col-md-4">
                    <strong>Total Final Value:</strong> Rs. {totals.finalValue.toFixed(2)}
                  </div>
                  
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={onClose}>
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default GstItemsModal;

